<template>
  <div>
    <SubCard
      :subCardCurrentDataLabel="$t('fahrleistung.card.dataLabel')"
      :subCardCurrentInfoText="[
        $t('fahrleistung.card.infoText', {
          aktuelleKilometer: formatKilometers(
            parseInt(motorContract.insuredVehicle.insuredMileage, 10)
          )
        })
      ]"
      :subCardTitle="$t('fahrleistung.card.title')"
    >
      <template v-slot:main>
        <div id="fahrleistung-input-row" class="card-row">
          <div
            id="neue-fahrleistung-pro-jahr-container"
            class="card-row__col fahrleistung-input-container"
          >
            <BasicTextfield
              @input="updateNewMileage"
              id="fahrleistung-form-new-mileage-per-year"
              :label="$t('fahrleistung.newMileage.label')"
              :status="newMileageStatus"
              :value="newMileage"
              @update:blur="sanitizenewMileage"
              @blur="sanitizenewMileage"
              @update:focus="formatnewMileage"
              @focus="formatnewMileage"
              @update:input="updateNewMileage"
            >
              <template v-if="showEmptyTextnewMileage" v-slot:info>
                {{ $t('fahrleistung.newMileage.empty') }}
              </template>
              <template v-slot:error>{{ newMileageError }}</template>
            </BasicTextfield>
          </div>
          <div class="card-row__col fahrleistung-input-container">
            <BasicTextfield
              @input="updateCurrentKmStanding"
              id="fahrleistung-form-new-mileage-fahrleistung-input-container"
              :label="$t('fahrleistung.currentKmStanding.label')"
              :status="currentKmStandingStatus"
              :value="currentKmStanding"
              inputmode="numeric"
              @update:blur="formatcurrentKmStanding"
              @blur="formatcurrentKmStanding"
              @update:focus="sanitizecurrentKmStanding"
              @focus="sanitizecurrentKmStanding"
              @update:input="updateCurrentKmStanding"
            >
              <template v-if="showEmptyTextcurrentKmStanding" v-slot:info>
                {{ $t('fahrleistung.currentKmStanding.empty') }}
              </template>
              <template v-slot:error>{{ currentKmStandingError }}</template>
            </BasicTextfield>
          </div>
        </div>
      </template>
    </SubCard>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, type PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { type MotorContract } from '@/types/motor/MotorContract'
import { BasicTextfield, InputStatus, SubCard } from 'de-zurich-ui-library'
import { type Fahrleistung } from '@/typesTenant/MotorContractChange'

const { t, n } = useI18n()

const props = defineProps({
  motorContract: {
    type: Object as PropType<MotorContract>,
    required: true
  },
  value: {
    type: Object as PropType<Fahrleistung>,
    required: true
  }
})

const status = ref<InputStatus>(InputStatus.DEFAULT)
const newMileage = ref('')
const newMileageError = ref('')
const newMileageStatus = ref<InputStatus>(InputStatus.DEFAULT)
const showEmptyTextnewMileage = ref(true)
const currentKmStanding = ref('')
const currentKmStandingError = ref('')
const currentKmStandingStatus = ref<InputStatus>(InputStatus.DEFAULT)
const showEmptyTextcurrentKmStanding = ref(true)
const kilometerMinValue = 1
const kilometerMaxValue = 999000
const newMileageMinValue = 1000
const numberRegEx: RegExp = new RegExp(/[^0-9]+/g)

const fahrerkreisValue = ref()

const emit = defineEmits<{
  status: [value: InputStatus]
  newMileageStatus: [value: InputStatus]
}>()
onMounted(() => {
  fahrerkreisValue.value = props.value
  emit('status', status.value)
})

watch(
  () => props.value,
  () => {
    if (props.value && props.value.newMileage) {
      fahrerkreisValue.value = props.value
      newMileage.value = ''
      currentKmStanding.value = ''
      emit('status', status.value)
    }
  }
)

function updateNewMileage(val: string) {
  newMileage.value = val
  validatenewMileage()
}

function validatenewMileage(): void {
  newMileage.value = newMileage.value.replace(numberRegEx, '')
  fahrerkreisValue.value.newMileage = parseInt(
    newMileage.value.replace(numberRegEx, ''),
    10
  )
  const currentMileage = parseInt(
    props.motorContract.insuredVehicle.insuredMileage.replace(numberRegEx, ''),
    10
  )

  if (isNaN(fahrerkreisValue.value.newMileage)) {
    newMileageError.value = t('fahrleistung.newMileage.empty') as string
    newMileageStatus.value = InputStatus.ERROR
  } else if (
    fahrerkreisValue.value.newMileage < newMileageMinValue ||
    fahrerkreisValue.value.newMileage > kilometerMaxValue
  ) {
    newMileageError.value = t('fahrleistung.validationMessages.notInRange', {
      minValue: formatKilometers(newMileageMinValue),
      maxValue: formatKilometers(kilometerMaxValue)
    }).toString()
    newMileageStatus.value = InputStatus.ERROR
  } else if (fahrerkreisValue.value.newMileage === currentMileage) {
    newMileageError.value = t('fahrleistung.validationMessages.sameMileage') as string
    newMileageStatus.value = InputStatus.ERROR
  } else {
    newMileageStatus.value = InputStatus.VALID
  }

  showEmptyTextnewMileage.value = newMileageStatus.value === InputStatus.VALID
  updateFormStatus()
}

function formatnewMileage(): void {
  newMileage.value = newMileage.value.replace(numberRegEx, '')
}

function sanitizenewMileage(): void {
  validatenewMileage()
  nextTick(() => {
    const newMileageNumber = parseInt(newMileage.value, 10)
    if (!isNaN(newMileageNumber)) {
      if (newMileageStatus.value === InputStatus.VALID) {
        fahrerkreisValue.value.newMileage = Math.round(newMileageNumber / 1000) * 1000
      }
      newMileage.value = formatKilometers(fahrerkreisValue.value.newMileage)
    }
  })
}

function updateCurrentKmStanding(val: string) {
  currentKmStanding.value = val
  validatecurrentKmStanding()
}

function validatecurrentKmStanding(): void {
  currentKmStanding.value = currentKmStanding.value.replace(numberRegEx, '')
  fahrerkreisValue.value.currentKmStanding = parseInt(
    currentKmStanding.value.replace(numberRegEx, ''),
    10
  )

  if (!fahrerkreisValue.value.currentKmStanding) {
    currentKmStandingError.value = t('fahrleistung.currentKmStanding.empty') as string

    currentKmStandingStatus.value = InputStatus.ERROR
  } else if (
    fahrerkreisValue.value.currentKmStanding < kilometerMinValue ||
    fahrerkreisValue.value.currentKmStanding > kilometerMaxValue
  ) {
    currentKmStandingError.value = t('fahrleistung.validationMessages.notInRange', {
      minValue: formatKilometers(kilometerMinValue),
      maxValue: formatKilometers(kilometerMaxValue)
    }).toString()
    currentKmStandingStatus.value = InputStatus.ERROR
  } else {
    currentKmStandingStatus.value = InputStatus.VALID
  }
  showEmptyTextcurrentKmStanding.value =
    currentKmStandingStatus.value === InputStatus.VALID
  updateFormStatus()
}

function sanitizecurrentKmStanding(): void {
  currentKmStanding.value = currentKmStanding.value.replace(numberRegEx, '')
}

function formatcurrentKmStanding(): void {
  validatecurrentKmStanding()
  nextTick(() => {
    const currentKmStandingNumber = parseInt(currentKmStanding.value, 10)
    if (!isNaN(currentKmStandingNumber)) {
      currentKmStanding.value = formatKilometers(currentKmStandingNumber)
    }
  })
}

function updateFormStatus(): void {
  status.value =
    newMileageStatus.value === InputStatus.VALID &&
    currentKmStandingStatus.value === InputStatus.VALID
      ? InputStatus.VALID
      : InputStatus.ERROR
  emit('status', status.value)
  emit('newMileageStatus', newMileageStatus.value)
}

function formatKilometers(kilometers: number): string {
  if (kilometers === undefined || kilometers === null) {
    return ''
  } else {
    return (
      n(kilometers, {
        locale: 'de-DE'
      }) + ' km'
    )
  }
}
</script>

<style lang="scss" scoped>
.card-row {
  column-gap: 32px;
}

.fahrleistung-input-container {
  width: 100%;
  @media (min-width: 600px) {
    max-width: 234px;
  }
}

#neue-fahrleistung-pro-jahr-container {
  @media (min-width: 600px) {
    margin-right: calc(var(--baseline) * 2);
  }
}
</style>
